import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { createUser, updateUser } from "services/users";
import planService from "services/plans";
import logger from "utils/logger";

export default function CardUserForm({ user, isOpen, onClose, onSaved }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    admin: false,
    plan_id: "",
  });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const isEditMode = !!user;

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      if (user) {
        setFormData({
          name: user.name || "",
          email: user.email || "",
          password: "",
          admin: user.admin || false,
          plan_id: user.plan_id || "",
        });
      } else {
        // Reset form for new user
        setFormData({
          name: "",
          email: "",
          password: "",
          admin: false,
          plan_id: "",
        });
      }
      setError(null);
      setValidationErrors({});
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [user, isOpen]);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  // Load plans when component mounts
  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await planService.getPlans(true);
      setPlans(response.data || []);
    } catch (err) {
      logger.error("Error loading plans:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    // Prepare data
    const dataToSend = { ...formData };

    // Don't send password if it's empty in edit mode
    if (isEditMode && !dataToSend.password) {
      delete dataToSend.password;
    }

    // Convert empty plan_id to null
    if (dataToSend.plan_id === "") {
      dataToSend.plan_id = null;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        await updateUser(user.id, dataToSend);
      } else {
        await createUser(dataToSend);
      }

      onSaved();
    } catch (err) {
      logger.error("Error saving user:", err);

      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      } else {
        setError(err.response?.data?.message || "Error al guardar el usuario");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Agregar estilos CSS para animación
  const spinnerStyle = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  return createPortal(
    <>
      <style>{spinnerStyle}</style>
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflowY: 'auto',
          zIndex: 100000,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem 1.5rem 1rem',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: '#374151' }}>
            {isEditMode ? "Editar Usuario" : "Nuevo Usuario"}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0.25rem'
            }}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
          {error && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              borderRadius: '0.375rem'
            }}>
              {error}
            </div>
          )}

          {/* Name */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              Nombre *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Juan Pérez"
              disabled={loading}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: validationErrors.name ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {validationErrors.name && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {validationErrors.name[0]}
              </p>
            )}
          </div>

          {/* Email */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="usuario@ejemplo.com"
              disabled={loading}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: validationErrors.email ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {validationErrors.email && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {validationErrors.email[0]}
              </p>
            )}
          </div>

          {/* Password */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              Contraseña {!isEditMode && "*"}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={isEditMode ? "Dejar en blanco para no cambiar" : "Mínimo 6 caracteres"}
              disabled={loading}
              required={!isEditMode}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: validationErrors.password ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {validationErrors.password && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {validationErrors.password[0]}
              </p>
            )}
            {isEditMode && (
              <p style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                Dejar en blanco para mantener la contraseña actual
              </p>
            )}
          </div>

          {/* Plan */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              Plan
            </label>
            <select
              name="plan_id"
              value={formData.plan_id}
              onChange={handleChange}
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: validationErrors.plan_id ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            >
              <option value="">Sin plan asignado</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
            {validationErrors.plan_id && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {validationErrors.plan_id[0]}
              </p>
            )}
          </div>

          {/* Admin checkbox */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}>
              <input
                type="checkbox"
                name="admin"
                checked={formData.admin}
                onChange={handleChange}
                disabled={loading}
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  marginRight: '0.5rem',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              />
              <div>
                <span style={{
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}>
                  Administrador
                </span>
                <p style={{
                  color: '#6b7280',
                  fontSize: '0.75rem',
                  marginTop: '0.25rem',
                  marginBottom: 0,
                  marginLeft: '1.75rem'
                }}>
                  Los administradores tienen acceso completo al sistema
                </p>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase',
                opacity: loading ? 0.5 : 1
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: loading ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: '1rem',
                    height: '1rem',
                    border: '2px solid #ffffff',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Guardando...
                </>
              ) : (
                <>
                  {isEditMode ? "Actualizar" : "Crear"} Usuario
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>,
    document.body
  );
}

CardUserForm.propTypes = {
  user: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
